import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectedCampaignVM } from 'features/src/lib/donations/types/campaigns.models';

type Html2Canvas = (element: HTMLElement, options: Record<string, unknown>) => Promise<HTMLCanvasElement>;

@Component({
  selector: 'don-campaign-story-card',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './campaign-story-card.component.html',
  styleUrls: ['./campaign-story-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignStoryCardComponent implements OnChanges {
  private readonly fallbackShareImagePath = 'assets/forSocialMedia.png';
  readonly brandLogoPath = 'assets/Image/Logo/logo-crezco-white.svg';
  private readonly storyWidth = 1080;
  private readonly storyHeight = 1920;
  private readonly exportMimeType = 'image/jpeg';
  private readonly exportQuality = 0.94;
  private readonly exportScaleCandidates = [1.8, 1.5, 1.25, 1];
  private readonly assetWaitTimeoutMs = 8000;
  private readonly minStoryProgressPct = 0.01;
  private readonly minVisibleStoryArcPct = 0.35;
  private html2canvasLoader?: Promise<Html2Canvas>;

  readonly progressGradientId = `story-progress-${Math.random().toString(36).slice(2, 9)}`;

  @Input() campaign: SelectedCampaignVM | null = null;
  @Input() donorsCount = 0;
  @ViewChild('storyExportCard') storyExportCard?: ElementRef<HTMLElement>;

  progressPctAnimated = 0;

  constructor(
    public translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaign']) {
      this.animateProgress();
    }
  }

  get currentAmount(): number {
    return this.toNumber(this.campaign?.currentAmount);
  }

  get targetAmount(): number {
    return this.toNumber(this.campaign?.targetAmount);
  }

  get remainingAmount(): number {
    return Math.max(0, this.targetAmount - this.currentAmount);
  }

  get progressPct(): number {
    if (this.targetAmount <= 0) return 0;
    return Math.max(0, Math.min(100, (this.currentAmount / this.targetAmount) * 100));
  }

  get storyProgressPct(): number {
    if (this.progressPct <= 0) return 0;
    return Math.max(this.minStoryProgressPct, Math.min(100, this.progressPct));
  }

  get storyProgressText(): string {
    const value = this.storyProgressPct;
    if (value <= 0) return '0';

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  get visualStoryProgressPct(): number {
    if (this.storyProgressPct <= 0) return 0;
    return Math.max(this.storyProgressPct, this.minVisibleStoryArcPct);
  }

  get storyHeroImageUrl(): string {
    return this.fallbackShareImagePath;
  }

  get progressRadius(): number {
    return 98;
  }

  get progressCircumference(): number {
    return 2 * Math.PI * this.progressRadius;
  }

  get progressDashOffset(): number {
    return this.progressCircumference * (1 - this.visualStoryProgressPct / 100);
  }

  get donorsLabel(): string {
    return this.translate.instant('CAMPAIGN.Story.DonorsLabel', { count: this.donorsCount });
  }

  get campaignContextText(): string {
    return this.translate.instant('CAMPAIGN.Story.Context');
  }

  get primaryStoryText(): string {
    return this.translate.instant('CAMPAIGN.Story.PrimaryText');
  }

  get storyHeadingLead(): string {
    return this.translate.instant('CAMPAIGN.Story.HeadingLead');
  }

  get storyHeadingAccent(): string {
    return this.translate.instant('CAMPAIGN.Story.HeadingAccent');
  }

  get storyPercentLabel(): string {
    return this.translate.instant('CAMPAIGN.Story.PercentLabel');
  }

  get storyTargetLabel(): string {
    return this.translate.instant('CAMPAIGN.Story.TargetLabel');
  }

  get storyRaisedLabel(): string {
    return this.translate.instant('CAMPAIGN.Story.RaisedLabel');
  }

  get storyRemainingLabel(): string {
    return this.translate.instant('CAMPAIGN.Story.RemainingLabel');
  }

  get storyDonateAtLabel(): string {
    return this.translate.instant('CAMPAIGN.Story.DonateAtLabel');
  }

  formatUsd(value: number): string {
    return `USD ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
      Number.isFinite(value) ? value : 0
    )}`;
  }

  onStoryImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.src = this.fallbackShareImagePath;
    image.style.objectFit = 'cover';
  }

  async buildShareImageFile(): Promise<File | null> {
    try {
      const cardEl = this.storyExportCard?.nativeElement;
      if (!cardEl) {
        return null;
      }

      await this.waitForStoryAssets(cardEl);

      // Wait until fonts/images are painted before rasterizing to avoid soft text edges.
      await this.waitForNextFrame();
      await this.waitForNextFrame();

      const canvas = await this.renderStoryCanvasWithRetries(cardEl);
      if (!canvas) return null;

      const normalizedCanvas = this.normalizeStoryCanvas(canvas);

      const blob = await this.canvasToBlob(normalizedCanvas, this.exportMimeType, this.exportQuality);
      if (!blob) return null;

      return new File([blob], 'crezco-campaign-story-instagram.jpg', {
        type: blob.type || this.exportMimeType,
      });
    } catch {
      return null;
    }
  }

  private async renderStoryCanvasWithRetries(cardEl: HTMLElement): Promise<HTMLCanvasElement | null> {
    for (const scale of this.exportScaleCandidates) {
      const canvas = await this.renderStoryCanvas(cardEl, scale);
      if (canvas) {
        return canvas;
      }

      await this.waitForNextFrame();
    }

    return null;
  }

  private async renderStoryCanvas(cardEl: HTMLElement, scale: number): Promise<HTMLCanvasElement | null> {
    try {
      const html2canvas = await this.loadHtml2Canvas();
      return await html2canvas(cardEl, {
        width: this.storyWidth,
        height: this.storyHeight,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#170A3D',
        scale,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        imageTimeout: 20000,
      });
    } catch {
      return null;
    }
  }

  private loadHtml2Canvas(): Promise<Html2Canvas> {
    if (this.html2canvasLoader) return this.html2canvasLoader;

    this.html2canvasLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
      script.async = true;
      script.onload = () => {
        const html2canvas = (window as Window & { html2canvas?: Html2Canvas }).html2canvas;
        html2canvas ? resolve(html2canvas) : reject(new Error('html2canvas is unavailable'));
      };
      script.onerror = () => reject(new Error('Unable to load html2canvas'));
      document.head.appendChild(script);
    });

    return this.html2canvasLoader;
  }

  private animateProgress(): void {
    this.progressPctAnimated = 0;
    this.cdr.markForCheck();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.progressPctAnimated = this.progressPct;
        this.cdr.markForCheck();
      });
    });
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const clean = value.replace(/,/g, '').replace(/\$/g, '').trim();
      const parsed = Number(clean);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private async waitForStoryAssets(root: HTMLElement): Promise<void> {
    const images = Array.from(root.querySelectorAll('img'));

    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete || !img.currentSrc) {
              resolve();
              return;
            }

            let settled = false;
            const done = () => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              resolve();
            };

            const timeoutId = window.setTimeout(done, this.assetWaitTimeoutMs);
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          })
      )
    );

    if ('fonts' in document) {
      const fontSet = (document as Document & { fonts: FontFaceSet }).fonts;
      await Promise.race([
        fontSet.ready,
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, this.assetWaitTimeoutMs);
        }),
      ]);
    }
  }

  private normalizeStoryCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
    if (source.width === this.storyWidth && source.height === this.storyHeight) {
      return source;
    }

    const canvas = document.createElement('canvas');
    canvas.width = this.storyWidth;
    canvas.height = this.storyHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return source;
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, 0, 0, this.storyWidth, this.storyHeight);

    return canvas;
  }

  private waitForNextFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    });
  }
}
